import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api.ts'
import { ReviewRunStatus, type AdversarialRun, type ReviewAssessment } from './adversarialReview.ts'

/**
 * The latest adversarial review of a conferência, and the two things a super
 * admin does with it: run one, and say whether it was right (#541).
 *
 * Followed by polling its row, like a golden-set run and for the same reason:
 * a review is minutes of model work, and a request held open that long is
 * closed underneath the browser. Unlike that run it is not tied to a modal --
 * a review started here is still there on the next visit, so the hook reads
 * the latest one on mount and only polls while one is going.
 */

/** A review takes minutes, so this is patient rather than eager. */
const EVERY_MS = 5000

export interface AdversarialReviewState {
  /** `undefined` until the first answer; `null` when nothing was ever run. */
  readonly run: AdversarialRun | null | undefined
  /** How many runs this conferência has had, the latest included. */
  readonly runs: number
  readonly error: string | undefined
  /** A start is on its way to the server. */
  readonly starting: boolean
  readonly start: () => Promise<void>
  readonly assess: (disagreement: string, assessment: ReviewAssessment | null) => Promise<void>
}

/**
 * @param enabled false for anybody but a super admin: the API answers them 404,
 *   and asking would only put a failed request in their console.
 * @param basis changes when the conferência is read or scored again, so the
 *   run's `stale` is asked for again instead of trusted from before.
 */
export function useAdversarialReview(
  verificationId: string,
  enabled: boolean,
  basis: unknown,
): AdversarialReviewState {
  const [run, setRun] = useState<AdversarialRun | null | undefined>()
  const [runs, setRuns] = useState(0)
  const [error, setError] = useState<string | undefined>()
  const [starting, setStarting] = useState(false)
  const live = useRef(true)

  const read = useCallback(async () => {
    try {
      const answer = await api.adversarialReview(verificationId)
      if (!live.current) return
      setRun(answer.run)
      setRuns(answer.runs)
      setError(undefined)
    } catch (cause) {
      if (live.current) setError(cause instanceof Error ? cause.message : 'falha ao ler a revisão')
    }
  }, [verificationId])

  useEffect(() => {
    live.current = true
    if (enabled) void read()
    return () => {
      live.current = false
    }
  }, [enabled, read, basis])

  const running = run?.status === ReviewRunStatus.Running
  useEffect(() => {
    if (!enabled || !running) return
    const timer = setInterval(() => void read(), EVERY_MS)
    return () => clearInterval(timer)
  }, [enabled, running, read])

  const start = useCallback(async () => {
    setStarting(true)
    try {
      await api.startAdversarialReview(verificationId)
      setError(undefined)
    } catch (cause) {
      if (live.current) {
        setError(cause instanceof Error ? cause.message : 'falha ao iniciar a revisão')
      }
    } finally {
      // Read either way: a refused start usually means one is already going,
      // and the row is what says so.
      await read()
      if (live.current) setStarting(false)
    }
  }, [verificationId, read])

  const assess = useCallback(
    async (disagreement: string, assessment: ReviewAssessment | null) => {
      if (run === null || run === undefined) return
      try {
        const answer = await api.assessDisagreement(run.id, disagreement, assessment)
        if (live.current) setRun({ ...run, assessments: answer.assessments })
      } catch (cause) {
        if (live.current) {
          setError(cause instanceof Error ? cause.message : 'falha ao registrar a avaliação')
        }
      }
    },
    [run],
  )

  return { run, runs, error, starting, start, assess }
}
